'use client'

import { useCartController } from 'cart/hooks/useCartController'
import { ButtonPrimary } from 'fukku/Button/Primary'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { ProductLocations } from 'product/types/ProductLocations'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'

import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import { useSizes } from '../../contexts/SizesProvider/SizesProvider'
import type { CommonProps } from '../../types/CommonProps'
import { getIsBackInStock } from '../../utils/getIsBackInStock/getIsBackInStock'
import { canAddProduct } from './utils/canAddProduct'

export type AddToBagProps = Pick<CommonProps, 'product' | 'look'> & {
	readonly location: ProductLocations
	readonly dataTestId?: string
	readonly className?: string
	readonly onError?: (error?: string) => void // TODO: remove error optional
}

export function AddToBagButton({
	product,
	look,
	onError,
	dataTestId,
	location,
	className,
}: AddToBagProps): React.ReactNode {
	const { t } = useLabels()
	const { isLessThanLarge, isLargeOrGreater } = useResponsive()
	const { selectedSize, setHasErrorMessage, openSheet, hasRelatedSizes } =
		useSizes()
	const { selectedColor } = useColors()
	const { add } = useCartController()
	const {
		country: { hasOnlineSale },
	} = useMasterData()

	const backInStockBtn = getIsBackInStock(
		hasRelatedSizes,
		hasOnlineSale,
		selectedSize,
		selectedColor
	)

	const handleAddToCart = (): void => {
		const canAddToBag = canAddProduct(
			selectedSize,
			isLargeOrGreater,
			selectedColor,
			hasRelatedSizes
		)

		if (canAddToBag) {
			add(
				{
					productId: product.id,
					colorId: selectedColor.id,
					sizeId: selectedSize?.id || '',
					seller: product.seller,
					lookId: look.id,
					lastUnits: !!selectedSize?.lastUnits,
				},
				location
			)
			return
		}

		if (isLessThanLarge) {
			openSheet(true)
			return
		}

		if (onError) {
			onError()
		}
		setHasErrorMessage(true)
	}

	if (backInStockBtn || !selectedColor.isSellable) {
		return null
	}

	return (
		<ButtonPrimary
			className={className}
			onClick={handleAddToCart}
			data-testid={dataTestId}
		>
			{t('pdp.add.button')}
		</ButtonPrimary>
	)
}
